import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tarotDropdownOpen, setTarotDropdownOpen] = useState(false);
  const [mobileTarotOpen, setMobileTarotOpen] = useState(true);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setTarotDropdownOpen(false);
  }, [location.pathname, location.hash]);

  // Click outside to close desktop dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTarotDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTarotActive = location.pathname.startsWith('/tarot');

  const headerBgClass = isHomePage
    ? isScrolled
      ? 'bg-[#381d6e]/95 backdrop-blur-md shadow-md text-white border-b border-[#5f2a99]'
      : 'bg-gradient-to-b from-[#381d6e]/85 via-[#381d6e]/40 to-transparent text-white'
    : 'bg-[#381d6e] text-white border-b border-[#5f2a99] shadow-sm';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 flex items-center justify-center text-[#faf5f9] group-hover:bg-[#d9238c]/50 transition-colors shrink-0">
              <i className="fa-solid fa-mountain-sun text-lg text-[#f7eefa]"></i>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-base sm:text-lg font-bold tracking-tight text-[#faf5f9] leading-tight group-hover:text-white transition-colors">
                Ibjerget.dk
              </span>
              <span className="text-[10px] sm:text-[11px] text-[#f7eefa] font-medium leading-tight">
                Golden Eagle Terapier
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1 2xl:gap-1.5 text-xs 2xl:text-sm font-medium">
            <NavLink
              to="/alt-ansigtsterapi"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              ALT Ansigtsterapi
            </NavLink>

            <NavLink
              to="/kropsterapi"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Kropsterapi
            </NavLink>

            <NavLink
              to="/healingmassage"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Healingmassage
            </NavLink>

            <NavLink
              to="/hotstone-massage"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Hotstone-massage
            </NavLink>

            {/* Tarotkortterapi Dropdown */}
            <div 
              className="relative group" 
              ref={dropdownRef}
              onMouseEnter={() => setTarotDropdownOpen(true)}
              onMouseLeave={() => setTarotDropdownOpen(false)}
            >
              <div 
                className={`flex items-center rounded-full transition-all duration-200 ${
                  isTarotActive
                    ? 'bg-[#d9238c] text-white font-semibold shadow-sm'
                    : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`}
              >
                <NavLink
                  to="/tarotkortterapi"
                  className="pl-2.5 2xl:pl-3 pr-1 py-2 whitespace-nowrap"
                >
                  Tarotkortterapi
                </NavLink>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTarotDropdownOpen((prev) => !prev);
                  }}
                  aria-expanded={tarotDropdownOpen}
                  aria-haspopup="true"
                  className="pr-2.5 2xl:pr-3 pl-1 py-2 flex items-center justify-center cursor-pointer opacity-80 hover:opacity-100 transition-opacity"
                  aria-label="Åbn undermenu for Tarotkortterapi"
                >
                  <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${tarotDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              {/* Submenu Dropdown */}
              {tarotDropdownOpen && (
                <div className="absolute left-0 top-full pt-1.5 w-72 z-50 animate-in fade-in duration-150">
                  <div className="rounded-2xl bg-[#4a2282] border border-[#5f2a99] shadow-xl py-2 text-xs overflow-hidden">
                    <div className="px-4 py-1.5 text-[10px] uppercase font-bold tracking-wider text-[#e895c8] border-b border-[#5f2a99]">
                      Vælg behandling v. Lone Hvid
                    </div>
                    <Link
                      to="/tarotkortterapi#chakraer"
                      onClick={() => setTarotDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[#f7eefa] hover:bg-[#d9238c] hover:text-white transition-colors group/item"
                    >
                      <div className="font-semibold text-white flex items-center justify-between">
                        <span>Status på dine chakraer</span>
                        <i className="fa-solid fa-arrow-right text-[10px] opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-0.5 transition-all"></i>
                      </div>
                      <div className="text-[11px] opacity-80">9 chakraer, 1-2 timer (500 kr.)</div>
                    </Link>
                    <Link
                      to="/tarotkortterapi#problemloesning"
                      onClick={() => setTarotDropdownOpen(false)}
                      className="block px-4 py-2.5 text-[#f7eefa] hover:bg-[#d9238c] hover:text-white transition-colors group/item"
                    >
                      <div className="font-semibold text-white flex items-center justify-between">
                        <span>Tarotkort-terapi med fokus på problemløsning</span>
                        <i className="fa-solid fa-arrow-right text-[10px] opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-0.5 transition-all"></i>
                      </div>
                      <div className="text-[11px] opacity-80">7-10 kort, dyb proces, 2-3 timer (700 kr.)</div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/spirituelle-boeger"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Spirituelle bøger
            </NavLink>

            <NavLink
              to="/foredrag"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Foredrag
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-2.5 2xl:px-3 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'text-[#f7eefa] hover:text-white hover:bg-white/10'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `ml-1 px-3.5 2xl:px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold shadow-sm' : 'bg-white/10 hover:bg-[#d9238c] text-white font-semibold border border-white/20'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Mobile / Tablet Menu Button (< 1280px / xl) */}
          <div className="flex xl:hidden items-center gap-3">
            <Link
              to="/kontakt"
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white shadow-sm transition-colors"
            >
              Kontakt
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors focus:outline-none"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Drawer Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-[#381d6e] border-b border-[#5f2a99] px-4 pt-3 pb-6 max-h-[85vh] overflow-y-auto shadow-2xl">
          <div className="space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/alt-ansigtsterapi"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              ALT Ansigtsterapi
            </NavLink>

            <NavLink
              to="/kropsterapi"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Kropsterapi
            </NavLink>

            <NavLink
              to="/healingmassage"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Healingmassage
            </NavLink>

            <NavLink
              to="/hotstone-massage"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Hotstone-massage
            </NavLink>

            {/* Mobile Tarotkortterapi with Submenu */}
            <div className="rounded-lg bg-[#4a2282]/60 border border-[#5f2a99]/60 overflow-hidden my-1">
              <div className="flex items-center justify-between px-3 py-2.5">
                <NavLink
                  to="/tarotkortterapi"
                  className={({ isActive }) =>
                    `text-sm font-medium flex-1 ${isActive ? 'text-[#e895c8] font-semibold' : 'text-[#f7eefa]'}`
                  }
                >
                  Tarotkortterapi
                </NavLink>
                <button
                  type="button"
                  onClick={() => setMobileTarotOpen(!mobileTarotOpen)}
                  className="p-1 text-[#f7eefa] hover:text-white"
                  aria-label="Fold tarot undermenu ud"
                >
                  <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileTarotOpen ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              {mobileTarotOpen && (
                <div className="pl-4 pr-2 pb-2 space-y-1 border-t border-[#5f2a99]/40 pt-1">
                  <Link
                    to="/tarotkortterapi#chakraer"
                    className="block px-3 py-2 rounded-md text-xs text-[#f7eefa] hover:bg-[#d9238c] hover:text-white transition-colors"
                  >
                    • Status på dine chakraer (500 kr.)
                  </Link>
                  <Link
                    to="/tarotkortterapi#problemloesning"
                    className="block px-3 py-2 rounded-md text-xs text-[#f7eefa] hover:bg-[#d9238c] hover:text-white transition-colors"
                  >
                    • Tarotkort-terapi m. problemløsning (700 kr.)
                  </Link>
                </div>
              )}
            </div>

            <NavLink
              to="/spirituelle-boeger"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Spirituelle bøger
            </NavLink>

            <NavLink
              to="/foredrag"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Foredrag
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? 'bg-[#d9238c] text-white font-semibold' : 'text-[#f7eefa] hover:bg-white/5'
                }`
              }
            >
              Kontakt
            </NavLink>
          </div>

          <div className="mt-4 pt-4 border-t border-[#5f2a99] flex flex-col gap-2">
            <div className="text-xs text-[#f7eefa] space-y-1">
              <p className="font-semibold text-white">Direkte kontakt til behandlerne:</p>
              <a href="tel:30297902" className="flex items-center gap-2 hover:text-white py-1">
                <i className="fa-solid fa-phone text-[#d9238c]"></i>
                <span>Lone Hvid (Tarot/Foredrag): 30 29 79 02</span>
              </a>
              <a href="tel:66157902" className="flex items-center gap-2 hover:text-white py-1">
                <i className="fa-solid fa-phone text-[#d9238c]"></i>
                <span>Niels Boye (Krop/Ansigt/Massage): 66 15 79 02</span>
              </a>
            </div>
            <Link
              to="/kontakt"
              className="mt-2 w-full text-center bg-[#d9238c] hover:bg-[#be1878] text-white py-2.5 rounded-xl font-medium text-xs shadow-md transition-colors"
            >
              Gå til Kontakt
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
